import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { GridRowId } from '@mui/x-data-grid-pro';

// Assicurati che i path di queste importazioni combacino col tuo progetto
import DynamicDataGridPro, { FlatRow } from './DynamicDataGrid';
import { flattenRows, unflattenRow } from '../../../../utils/tablesUtils';
import {
    EditorialAdditionalInfo,
    EditorialAssetRenderParameters,
} from '../../../../types/editorialAssetsTypes';

// --- TIPI DELLE PROPS ---
interface DataGridBridgeProps {
    fieldDef: EditorialAssetRenderParameters;
    value: any[]; // L'array di righe "raw" che arriva da React Hook Form
    onChange: (newRawData: any[]) => void;
    isEditing: boolean;
    header?: EditorialAdditionalInfo
}

// --- COMPONENTE PONTE ---
const DataGridBridge: React.FC<DataGridBridgeProps> = ({
                                                           header,
                                                           fieldDef,
                                                           value,
                                                           onChange,
                                                           isEditing
                                                       }) => {
    // Stato piatto locale per conservare gli ID.
    // Tipizziamo lo stato esplicitamente come array di FlatRow
    const [flatRows, setFlatRows] = useState<FlatRow[]>(() => flattenRows(value || []));

    // useRef tipizzato a boolean
    const isInternalUpdate = useRef<boolean>(false);

    useEffect(() => {
        if (isInternalUpdate.current) {
            isInternalUpdate.current = false;
            return;
        }
        setFlatRows(flattenRows(value || []));
    }, [value]);

    const updateStateAndForm = (newFlatRows: FlatRow[]) => {
        isInternalUpdate.current = true;
        setFlatRows(newFlatRows);

        // Se `columns` non è definito, passiamo un array vuoto come fallback sicuro
        const newRawData = newFlatRows.map(row => unflattenRow(row, fieldDef.columns || []));
        onChange(newRawData);
    };

    const handleGridAddRow = (newFlatRow: FlatRow) => {
        updateStateAndForm([...flatRows, newFlatRow]);
    };

    const handleGridEditRow = (id: GridRowId, updatedFlatRow: FlatRow) => {
        updateStateAndForm(flatRows.map(r => r.id === id ? updatedFlatRow : r));
    };

    const handleGridDeleteRow = (id: GridRowId) => {
        updateStateAndForm(flatRows.filter(r => r.id !== id));
    };

    return (
        <Box sx={{
            opacity: isEditing ? 1 : 0.7,
            pointerEvents: isEditing ? 'auto' : 'none',
            // Per evitare che la tabella si comprima troppo se non ha un contenitore flessibile
            width: '100%'
        }}>
            <DynamicDataGridPro
                columns={fieldDef.columns || []}
                header={header}
                rows={flatRows}
                logo="https://via.placeholder.com/30/0000FF/FFFFFF?text=PES"
                onAddRow={handleGridAddRow}
                onEditRow={handleGridEditRow}
                onDeleteRow={handleGridDeleteRow}
                actionsWidth={40}
            />
        </Box>
    );
};

export default DataGridBridge;